/**
 * CHANGE MY PASSWORD — and the gate that makes it compulsory for anybody still
 * on the shared starting password. Added 14-10-2026.
 *
 * WHY
 * Since 10-10-2026 ims-test.ovalprojects.com is the live site, and every
 * account was created with the same starting password. Until today nobody
 * could change their own: only an administrator could, from the Supabase
 * dashboard.
 *
 * WHAT IT DOES
 *   - A "Password" button beside Sign out, in all three modules. It asks for
 *     the current password, then the new one twice.
 *   - The current password is CHECKED (by signing in with it) before anything
 *     changes, so a PC left signed in cannot have its owner's password changed
 *     by whoever walks up to it.
 *   - New-password rules (decided 14-10-2026): at least 8 characters, at least
 *     one letter and one number, different from the current one, and never the
 *     shared starting password.
 *   - After the change, every OTHER device signed in as that person is signed
 *     out; the device used for the change stays signed in.
 *   - Whoever signs in WITH the shared starting password meets this form first,
 *     and cannot use the app until they have set their own (ForcedPasswordGate).
 *
 * THE SHARED STARTING PASSWORD IS NOT IN THIS FILE
 * Only its SHA-256 fingerprint is. A fingerprint cannot be turned back into the
 * password, so the website's files do not carry it in readable form; the app
 * fingerprints what was typed and compares the two.
 *
 * No database change: Supabase's own `auth.updateUser` changes the password,
 * and Supabase records the change in its own auth audit log.
 */
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Eye, EyeOff, KeyRound, LogOut, ShieldAlert, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { Alert, Modal, Spinner, useToast } from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'

/** SHA-256 of the shared starting password every account was created with. */
const SHARED_START_SHA256 = 'f3731233692bf391626424af65f1116ce7f1299ec618b28fe1821651b5f12fb8'

/** True when `pw` is the shared starting password. Never throws. */
export async function isSharedStartingPassword(pw: string): Promise<boolean> {
  try {
    if (!pw || !globalThis.crypto?.subtle) return false
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(pw))
    const hex = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('')
    return hex === SHARED_START_SHA256
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Passwords the ADMINISTRATOR hands out (Users & Roles: Add user, Reset).
// Added 14-10-2026. The same rules as a person's own new password, so a
// temporary password is never weaker than the one that will replace it. The
// server (admin-provision-users) enforces them again.
// ---------------------------------------------------------------------------

/** A sentence saying what is wrong with `pw`, or null when it is acceptable. */
export async function passwordProblem(pw: string): Promise<string | null> {
  if (!pw || pw.length < 8) return 'Use at least 8 characters.'
  if (!/[A-Za-z]/.test(pw)) return 'Include at least one letter.'
  if (!/\d/.test(pw)) return 'Include at least one number.'
  if (await isSharedStartingPassword(pw)) return 'That is the old shared starting password, which is no longer allowed.'
  return null
}

const WORDS = [
  'Kite', 'Moss', 'River', 'Cedar', 'Lamp', 'Stone', 'Maple', 'Cloud', 'Brick', 'Tiger',
  'Pearl', 'Orbit', 'Plain', 'Delta', 'Ember', 'Grove', 'Harbor', 'Ivory', 'Jade', 'Lotus',
  'Mango', 'Noble', 'Ocean', 'Pine', 'Quartz', 'Reef', 'Sage', 'Tulip', 'Umber', 'Valley',
  'Willow', 'Zinc', 'Amber', 'Bison', 'Coral', 'Dune', 'Falcon', 'Garnet', 'Hill', 'Iris',
]

/**
 * A random temporary password such as "Kite-4827-Moss": easy to read out over
 * the phone, impossible to guess, and it passes the rules above. Uses the
 * browser's cryptographic random numbers, not Math.random.
 */
export function generateTempPassword(): string {
  const r = new Uint32Array(3)
  crypto.getRandomValues(r)
  const w1 = WORDS[r[0] % WORDS.length]
  let w2 = WORDS[r[1] % WORDS.length]
  if (w2 === w1) w2 = WORDS[(r[1] + 1) % WORDS.length]
  return `${w1}-${String(1000 + (r[2] % 9000))}-${w2}`
}

// ---------------------------------------------------------------------------
// "This person must change their password before using the app."
//
// Kept in localStorage against the sign-in EMAIL (not the password, never the
// password), so it survives a refresh and a second tab, and does not catch a
// different person signing in on the same PC. Wrapped in try/catch: private
// browsing and some managed Android builds make localStorage throw.
// ---------------------------------------------------------------------------
const MUST_KEY = 'oval.mustChangePassword'
const MUST_EVENT = 'oval-must-change-password'

export function markMustChangePassword(email: string) {
  try { localStorage.setItem(MUST_KEY, email.trim().toLowerCase()) } catch { /* see above */ }
  window.dispatchEvent(new Event(MUST_EVENT))
}

export function clearMustChangePassword() {
  try { localStorage.removeItem(MUST_KEY) } catch { /* see above */ }
  window.dispatchEvent(new Event(MUST_EVENT))
}

function mustChangeFor(email: string | null | undefined): boolean {
  if (!email) return false
  try { return localStorage.getItem(MUST_KEY) === email.trim().toLowerCase() } catch { return false }
}

// ---------------------------------------------------------------------------
// The form, used by both the button's pop-up and the compulsory gate.
// ---------------------------------------------------------------------------
function PasswordForm({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const { profile } = useAuth()
  const toast = useToast()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [nextIsShared, setNextIsShared] = useState(false)

  useEffect(() => {
    let live = true
    void isSharedStartingPassword(next).then((v) => { if (live) setNextIsShared(v) })
    return () => { live = false }
  }, [next])

  const rules = useMemo(() => [
    { ok: next.length >= 8, text: 'At least 8 characters' },
    { ok: /[A-Za-z]/.test(next), text: 'At least one letter' },
    { ok: /\d/.test(next), text: 'At least one number' },
    { ok: next.length > 0 && next !== current, text: 'Different from your current password' },
    { ok: next.length > 0 && !nextIsShared, text: 'Not the shared starting password' },
    { ok: again.length > 0 && again === next, text: 'Both new password boxes match' },
  ], [next, again, current, nextIsShared])
  const allOk = current.length > 0 && rules.every((r) => r.ok)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    if (!profile?.email) { setErr('Your sign-in could not be read. Sign out and sign in again.'); return }
    if (!navigator.onLine) { setErr('Changing your password needs an internet connection.'); return }
    if (!allOk) { setErr('Fill in all three boxes so that every rule below shows a tick.'); return }

    setBusy(true)
    try {
      // 1. Prove the current password. Signing in again as yourself replaces
      //    this device's session with a fresh one; nothing else changes.
      const { error: e1 } = await supabase.auth.signInWithPassword({
        email: profile.email, password: current,
      })
      if (e1) {
        throw new Error(/invalid login credentials/i.test(e1.message)
          ? 'Your current password is not correct. Check the caps-lock key and try again.'
          : e1.message)
      }

      // 2. Set the new one, and clear the "temporary password" mark the
      //    administrator's Add user / Reset put on the account (if any).
      const { error: e2 } = await supabase.auth.updateUser({
        password: next, data: { must_change_password: false },
      })
      if (e2) {
        throw new Error(/different from the old/i.test(e2.message)
          ? 'The new password must be different from the current one.'
          : e2.message)
      }

      // 3. Sign out every OTHER device signed in as this person. If this one
      //    step fails the password has still changed, so say so rather than
      //    reporting the whole change as a failure.
      const { error: e3 } = await supabase.auth.signOut({ scope: 'others' })

      clearMustChangePassword()
      if (e3) {
        toast.success('Password changed. Other devices could not be signed out automatically - sign out on them yourself.')
      } else {
        toast.success('Password changed. Any other device signed in as you has been signed out.')
      }
      onDone()
    } catch (x) {
      setErr(x instanceof Error ? x.message : 'The password could not be changed.')
    } finally {
      setBusy(false)
    }
  }

  const field = 'field pr-10'
  const type = show ? 'text' : 'password'

  return (
    <form onSubmit={save} className="space-y-4">
      {err && <Alert tone="error">{err}</Alert>}

      <div>
        <label className="label" htmlFor="cp-current">Current password</label>
        <input id="cp-current" className={field} type={type} autoComplete="current-password"
               value={current} onChange={(e) => setCurrent(e.target.value)} autoFocus />
      </div>

      <div>
        <label className="label" htmlFor="cp-next">New password</label>
        <div className="relative">
          <input id="cp-next" className={field} type={type} autoComplete="new-password"
                 value={next} onChange={(e) => setNext(e.target.value)} />
          <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  title={show ? 'Hide the passwords' : 'Show the passwords'}
                  aria-label={show ? 'Hide the passwords' : 'Show the passwords'}>
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div>
        <label className="label" htmlFor="cp-again">New password again</label>
        <input id="cp-again" className={field} type={type} autoComplete="new-password"
               value={again} onChange={(e) => setAgain(e.target.value)} />
      </div>

      <ul className="grid gap-1 rounded-lg bg-slate-50 p-3 text-xs sm:grid-cols-2">
        {rules.map((r) => (
          <li key={r.text} className={clsx('flex items-center gap-1.5', r.ok ? 'text-emerald-700' : 'text-slate-500')}>
            {r.ok ? <Check className="h-3.5 w-3.5 shrink-0" /> : <X className="h-3.5 w-3.5 shrink-0" />}
            {r.text}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap justify-end gap-2">
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        )}
        <button type="submit" className="btn-primary" disabled={busy || !allOk}>
          {busy ? <Spinner /> : <KeyRound className="h-4 w-4" />} Change password
        </button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// The button beside Sign out, in all three modules.
// ---------------------------------------------------------------------------
export function ChangePasswordButton() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
              title="Change your password"
              className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-800">
        <KeyRound className="h-4 w-4" />
        {/* The word is dropped on a narrow phone, as Sign out's is. */}
        <span className="hidden sm:inline">Password</span>
      </button>
      {/* Drawn straight onto the page (a portal), NOT inside the top bar. The
          top bar has backdrop-blur, and a blurred box becomes the "whole
          screen" for anything fixed inside it - which squeezed this pop-up
          into the height of the bar (reported 14-10-2026). */}
      {open && createPortal(
        <Modal open onClose={() => setOpen(false)} title="Change your password"
               subtitle="Your new password works on every device the next time you sign in.">
          <PasswordForm onDone={() => setOpen(false)} onCancel={() => setOpen(false)} />
        </Modal>,
        document.body,
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// The compulsory version. Rendered once, in App.tsx's Protected wrapper, so it
// covers all three modules and the module chooser. No close button: the only
// ways out are setting a new password or signing out.
// ---------------------------------------------------------------------------
export function ForcedPasswordGate() {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const [must, setMust] = useState(() => mustChangeFor(profile?.email))
  // 14-10-2026: also compulsory for a TEMPORARY password the administrator
  // handed out (Add user / Reset). The server marks those accounts with
  // user_metadata.must_change_password = true; the change clears it.
  const [temporary, setTemporary] = useState(false)

  useEffect(() => {
    const check = () => setMust(mustChangeFor(profile?.email))
    check()
    window.addEventListener(MUST_EVENT, check)
    window.addEventListener('storage', check)
    return () => {
      window.removeEventListener(MUST_EVENT, check)
      window.removeEventListener('storage', check)
    }
  }, [profile?.email])

  useEffect(() => {
    let live = true
    if (!profile?.id) { setTemporary(false); return }
    // Asked of the server, not read from the cached session, so a mark set
    // after this device last signed in is still seen.
    void supabase.auth.getUser().then(({ data }) => {
      if (live) setTemporary(data.user?.user_metadata?.must_change_password === true)
    }).catch(() => { /* offline: the shared-password check above still applies */ })
    return () => { live = false }
  }, [profile?.id])

  const done = () => { setMust(false); setTemporary(false) }

  const locked = must || temporary

  useEffect(() => {
    if (!locked) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [locked])

  if (!locked || !profile) return null

  // A portal for the same reason as the button's pop-up: wherever this is
  // mounted, it must cover the whole screen and nothing may clip it.
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/60 p-0 sm:items-center sm:p-4"
         role="dialog" aria-modal="true" aria-labelledby="forced-pw-title">
      <div className="flex max-h-[95vh] w-full flex-col overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-xl">
        <header className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <h2 id="forced-pw-title" className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <ShieldAlert className="h-5 w-5 text-amber-600" /> Set your own password to continue
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Signed in as {profile.full_name}
          </p>
        </header>
        <div className="space-y-4 px-4 py-4 sm:px-5">
          {must ? (
            <Alert tone="warning" title="You signed in with the shared starting password">
              Everyone's account began with the same password, so anyone who knows it could sign in
              as you. Choose a password only you know - it takes a minute. In the first box, type the
              password you have just signed in with.
            </Alert>
          ) : (
            <Alert tone="warning" title="You signed in with a temporary password">
              The administrator set this password for you, so somebody else knows it. Choose a
              password only you know - it takes a minute. In the first box, type the temporary
              password you have just signed in with.
            </Alert>
          )}
          <PasswordForm onDone={done} />
          <div className="border-t border-slate-200 pt-3 text-center">
            <button type="button"
                    onClick={async () => { await signOut(); navigate('/login') }}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700">
              <LogOut className="h-4 w-4" /> Not now - sign out instead
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
